import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from './ui/dialog';
import { useCreateNotification } from '../hooks/use-notification';
import { useDepartments } from '../hooks/use-department';
import { useToast } from './ui/toast';

interface SendNotificationFormData {
  title: string;
  text: string;
}

export default function SendNotificationForm({
  organizationId,
}: {
  organizationId: number;
}) {
  const createNotification = useCreateNotification();
  const { data: departmentsData } = useDepartments(organizationId);
  const { showToast } = useToast();

  const [isOrgWide, setIsOrgWide] = useState(true);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<number[]>(
    [],
  );
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<SendNotificationFormData>();

  const titleValue = watch('title', '');
  const textValue = watch('text', '');
  const departments = departmentsData?.departments || [];

  const toggleDepartment = (departmentId: number) => {
    setSelectedDepartmentIds((prev) =>
      prev.includes(departmentId)
        ? prev.filter((id) => id !== departmentId)
        : [...prev, departmentId],
    );
  };

  const onSubmit = () => {
    if (!isOrgWide && selectedDepartmentIds.length === 0) {
      showToast('Select at least one department', 'error');
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirmSend = () => {
    const data = { title: titleValue, text: textValue };

    createNotification.mutate(
      {
        title: data.title,
        text: data.text,
        isOrgWide,
        departmentIds: isOrgWide ? undefined : selectedDepartmentIds,
        organisationId: organizationId,
      },
      {
        onSuccess: () => {
          showToast('Notification sent', 'success');
          reset();
          setSelectedDepartmentIds([]);
          setIsOrgWide(true);
          setShowConfirm(false);
        },
        onError: () => {
          showToast('Failed to send notification', 'error');
          setShowConfirm(false);
        },
      },
    );
  };

  const getTargetLabel = () => {
    if (isOrgWide) return 'Entire Organisation';

    const selectedNames = departments
      .filter((d) => selectedDepartmentIds.includes(d.id))
      .map((d) => d.name);

    return selectedNames.join(', ');
  };

  return (
    <Card className="border shadow-none rounded-lg">
      <CardContent className="p-4">
        <h3 className="text-sm font-medium mb-4">Send Notification</h3>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="title" className="text-xs">
                Title
              </Label>
              <span className="text-xs text-muted-foreground">
                {titleValue.length}/50
              </span>
            </div>
            <Input
              id="title"
              {...register('title', {
                required: 'Title is required',
                maxLength: { value: 50, message: 'Title is too long' },
              })}
              maxLength={50}
              placeholder="Notification title"
              className="h-9 text-sm"
            />
            {errors.title && (
              <p className="text-xs text-red-500">{errors.title.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="text" className="text-xs">
                Message
              </Label>
              <span className="text-xs text-muted-foreground">
                {textValue.length}/2000
              </span>
            </div>
            <Textarea
              id="text"
              {...register('text', {
                required: 'Message is required',
                maxLength: { value: 2000, message: 'Message is too long' },
              })}
              maxLength={2000}
              placeholder="Write your notification message..."
              className="text-sm min-h-[120px]"
            />
            {errors.text && (
              <p className="text-xs text-red-500">{errors.text.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Target</Label>
            <RadioGroup
              value={isOrgWide ? 'org' : 'department'}
              onValueChange={(value) => setIsOrgWide(value === 'org')}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="org" id="target-org" />
                <Label htmlFor="target-org" className="text-sm font-normal">
                  Entire Organisation
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="department" id="target-department" />
                <Label
                  htmlFor="target-department"
                  className="text-sm font-normal"
                >
                  Specific Departments
                </Label>
              </div>
            </RadioGroup>
          </div>

          {!isOrgWide && (
            <div className="space-y-2">
              <Label className="text-xs">Departments</Label>
              {departments.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No departments found
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {departments.map((department) => (
                    <button
                      key={department.id}
                      type="button"
                      onClick={() => toggleDepartment(department.id)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        selectedDepartmentIds.includes(department.id)
                          ? 'bg-accent-purple text-white border-accent-purple'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {department.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={createNotification.isPending}
              className="bg-accent-purple hover:bg-accent-purple/90 text-white text-sm px-6"
            >
              {createNotification.isPending
                ? 'Sending...'
                : 'Send Notification'}
            </Button>
          </div>
        </form>
      </CardContent>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send this notification?</DialogTitle>
            <DialogDescription>
              This will be sent immediately and cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <p className="text-xs text-muted-foreground">Title</p>
              <p className="text-sm font-medium">{titleValue}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Message</p>
              <p className="text-sm line-clamp-3">{textValue}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Sending to</p>
              <p className="text-sm font-medium">{getTargetLabel()}</p>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="text-sm">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleConfirmSend}
              disabled={createNotification.isPending}
              className="bg-accent-purple hover:bg-accent-purple/90 text-white text-sm"
            >
              {createNotification.isPending ? 'Sending...' : 'Confirm & Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
